import { create } from "zustand";

import { DEFAULT_LAYOUT } from "../features/studio/layouts";
import type { BannerPreset } from "../features/profile/banner";

/**
 * Whether the studio is open, and how it is set up.
 *
 * ## Why this is not component state
 *
 * It was, and that made a broadcast a property of the console page: navigating
 * anywhere - a profile, the inbox, a track - unmounted the studio, which
 * stopped the camera, tore down the peer connection and ended the show. A host
 * who clicked a notification lost their set.
 *
 * The engine now lives in the app shell, above the router, the way the audio
 * player already does. That means the two ends of it - the shell that runs it
 * and the console that draws its controls - need somewhere to agree, and this
 * is that place.
 *
 * ## Settings live here too
 *
 * Layout, background and levels are read by the compositor in the shell and
 * written by the panel in the console. Keeping them next to `open` means the
 * studio survives navigation *as it was configured*, rather than resetting to
 * the default layout every time the host looks at something.
 *
 * Devices and the connection are deliberately *not* here: they are objects with
 * lifetimes rather than values, and they belong to the engine.
 */
interface StudioState {
  /** Whether the host has opened the studio. Not whether it is broadcasting. */
  open: boolean;

  layoutId: string;
  showCard: boolean;
  showBrand: boolean;
  background: BannerPreset | null;
  deckLevel: number;
  micLevel: number;
  /** Empty means the system default. */
  camera: string;
  microphone: string;
  /** Whether the host hears the deck through their own speakers. */
  monitoring: boolean;

  setOpen: (open: boolean) => void;
  set: <K extends keyof StudioSettings>(
    key: K,
    value: StudioSettings[K],
  ) => void;
}

type StudioSettings = Omit<StudioState, "open" | "setOpen" | "set">;

export const useStudioSettings = create<StudioState>()((set) => ({
  open: false,

  layoutId: DEFAULT_LAYOUT.id,
  showCard: true,
  showBrand: true,
  background: null,
  deckLevel: 0.8,
  micLevel: 1,
  camera: "",
  microphone: "",
  monitoring: true,

  setOpen: (open) => set({ open }),
  set: (key, value) => set({ [key]: value } as Partial<StudioState>),
}));
